import React, { Fragment, useEffect, useReducer } from "react";
import { Collapse } from "react-bootstrap";
import { Link } from "react-router-dom";
import { MenuList } from "./Menu";
import { useSelector } from "react-redux";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const reducer = (previousState, updatedState) => ({
  ...previousState,
  ...updatedState,
});

const initialState = {
  active: "",
  activeSubmenu: "",
};

// Clean tooltip styles with no conflicts
const tooltipStyles = `
  /* Reset any existing tooltip arrow styles */
  .tooltip .tooltip-arrow,
  .tooltip .tooltip-arrow::before {
    all: revert;
  }
  
  /* Custom tooltip styling */
  .custom-tooltip .tooltip-inner {
    background-color: #2c3e50 !important;
    color: #fff !important;
    font-size: 12px !important;
    padding: 8px 12px !important;
    border-radius: 6px !important;
    box-shadow: 0 4px 12px rgba(0,0,0,0.2) !important;
    max-width: 200px !important;
  }
  
  /* Top placement arrow - let Bootstrap handle the positioning, just change color */
  .custom-tooltip.bs-tooltip-top .tooltip-arrow::before,
  .bs-tooltip-top .tooltip-arrow::before {
    border-top-color: #2c3e50 !important;
  }
  
  /* Right placement arrow */
  .custom-tooltip.bs-tooltip-right .tooltip-arrow::before,
  .bs-tooltip-right .tooltip-arrow::before {
    border-right-color: #2c3e50 !important;
  }
  
  /* Bottom placement arrow */
  .custom-tooltip.bs-tooltip-bottom .tooltip-arrow::before,
  .bs-tooltip-bottom .tooltip-arrow::before {
    border-bottom-color: #2c3e50 !important;
  }
  
  /* Left placement arrow */
  .custom-tooltip.bs-tooltip-left .tooltip-arrow::before,
  .bs-tooltip-left .tooltip-arrow::before {
    border-left-color: #2c3e50 !important;
  }
`;

const SideBar = () => {
  const [state, setState] = useReducer(reducer, initialState);

  const userDetails = useSelector((state) => state.auth.userDetails);
  const allowedModuleIds =
    userDetails?.role_modules?.modules?.map((mod) => mod.module_id) || [];
  const roleName = userDetails?.role_name?.toLowerCase() || "";

  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(
    document.body.classList.contains("deznav-folded")
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsSidebarCollapsed(
        document.body.classList.contains("deznav-folded")
      );
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    const styleSheet = document.createElement("style");
    styleSheet.innerText = tooltipStyles;
    document.head.appendChild(styleSheet);

    return () => {
      observer.disconnect();
      styleSheet.remove();
    };
  }, []);

  const getFilteredMenuList = (menuList, allowedModuleIds) => {
    return menuList
      .map((menu) => {
        if (menu.content && Array.isArray(menu.content)) {
          const filteredContent = menu.content.filter((item) =>
            allowedModuleIds.includes(item.module_id)
          );

          return filteredContent.length > 0
            ? { ...menu, content: filteredContent }
            : null;
        }

        return allowedModuleIds.includes(menu.module_id) ? menu : null;
      })
      .filter(Boolean);
  };

  const filteredMenuList = React.useMemo(() => {
    if (roleName === "admin") {
      const flatDashboard = {
        title: "Dashboard",
        to: "/dashboard",
        iconStyle: <i className="flaticon-381-networking"></i>,
        tooltip: "Dashboard",
      };

      const remainingMenus = MenuList.filter(
        (menu) => menu.title !== "Dashboard"
      );

      return [flatDashboard, ...remainingMenus];
    }

    const dashboardMenu = MenuList.find(
      (menu) => menu.title === "Dashboard"
    );

    let dashboardItem = null;

    if (dashboardMenu && dashboardMenu.content) {
      const matched = dashboardMenu.content.find(
        (submenu) => submenu.title.toLowerCase() === roleName
      );

      if (matched) {
        dashboardItem = {
          title: "Dashboard",
          to: matched.to,
          iconStyle: matched.iconStyle,
          tooltip: `Dashboard - ${matched.title}`,
        };
      }
    }

    const otherMenus = getFilteredMenuList(MenuList, allowedModuleIds);

    return dashboardItem ? [dashboardItem, ...otherMenus] : otherMenus;
  }, [roleName, allowedModuleIds]);

  const handleMenuActive = (status) => {
    setState({ active: status });
    if (state.active === status) {
      setState({ active: "" });
    }
  };

  const handleSubmenuActive = (status) => {
    setState({ activeSubmenu: status });
    if (state.activeSubmenu === status) {
      setState({ activeSubmenu: "" });
    }
  };

  let path = window.location.pathname.split("/").pop();

  useEffect(() => {
    filteredMenuList.forEach((data) => {
      data.content?.forEach((item) => {
        if (path === item.to) {
          setState({ active: data.title });
        }
        item.content?.forEach((ele) => {
          if (path === ele.to) {
            setState({ activeSubmenu: item.title, active: data.title });
          }
        });
      });
    });
  }, [path, filteredMenuList]);

  const renderMenuItemWithTooltip = (data, children, placement = "top") => {
    const tooltipText = data.tooltip || data.title;

    return (
      <OverlayTrigger
        placement={placement}
        delay={{ show: 200, hide: 100 }}
        popperConfig={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ],
        }}
        overlay={
          <Tooltip 
            id={`tooltip-${data.title.replace(/\s+/g, "-").toLowerCase()}`}
            className="custom-tooltip"
          >
            {tooltipText}
          </Tooltip>
        }
      >
        {children}
      </OverlayTrigger>
    );
  };

  return (
    <div className="deznav">
      <div className="deznav-scroll">
        <ul className="metismenu" id="menu">
          {filteredMenuList.map((data, index) => {
            let menuClass = data.classsChange;

            if (menuClass === "menu-title") {
              return (
                <li className={menuClass} key={index}>
                  {data.title}
                </li>
              );
            }

            return (
              <li
                className={`has-menu ${
                  state.active === data.title ? "mm-active" : ""
                } ${data.to === path ? "mm-active" : ""}`}
                key={index}
              >
                {data.content && data.content.length > 1 ? (
                  <Fragment>
                    {/* MAIN MENU WITH TOOLTIP */}
                    {renderMenuItemWithTooltip(
                      data,
                      <Link
                        to={"#"}
                        className="has-arrow ai-icon"
                        onClick={() => handleMenuActive(data.title)}
                      >
                        {data.iconStyle}
                        <span className="nav-text">{data.title}</span>
                      </Link>,
                      "top"
                    )}

                    <Collapse in={state.active === data.title}>
                      <ul className={`${menuClass === "mm-collapse" ? "mm-show" : ""}`}>
                        {data.content.map((submenu, index) => (
                          <li
                            key={index}
                            className={`${
                              state.activeSubmenu === submenu.title
                                ? "mm-active"
                                : ""
                            }`}
                          >
                            {submenu.content && submenu.content.length > 0 ? (
                              <>
                                {/* SUBMENU WITHOUT TOOLTIP */}
                                <Link
                                  to={submenu.to}
                                  className={submenu.hasMenu ? "has-arrow" : ""}
                                  onClick={() =>
                                    handleSubmenuActive(submenu.title)
                                  }
                                >
                                  {submenu.title}
                                </Link>

                                <Collapse
                                  in={
                                    state.activeSubmenu === submenu.title
                                  }
                                >
                                  <ul>
                                    {submenu.content.map((nested, idx) => (
                                      <li key={idx}>
                                        {/* NESTED SUBMENU WITHOUT TOOLTIP */}
                                        <Link
                                          to={nested.to}
                                          className={
                                            path === nested.to
                                              ? "mm-active"
                                              : ""
                                          }
                                        >
                                          {nested.title}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                </Collapse>
                              </>
                            ) : (
                              <Link
                                to={submenu.to}
                                className={
                                  submenu.to === path ? "mm-active" : ""
                                }
                              >
                                {submenu.title}
                              </Link>
                            )}
                          </li>
                        ))}
                      </ul>
                    </Collapse>
                  </Fragment>
                ) : (
                  renderMenuItemWithTooltip(
                    data,
                    <Link
                      to={data.content?.[0]?.to || data.to}
                      className="ai-icon"
                    >
                      {data.iconStyle}
                      <span className="nav-text">{data.title}</span>
                    </Link>,
                    "top"
                  )
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default SideBar;